// PWA lifecycle: register the service worker, offer (not force) updates, and
// proactively warm the OpenCV engine cache so the FIRST scan also works offline.
//
// The engine (~10 MB) is runtime-cached (CacheFirst), not precached, to keep
// install lean for iOS (see vite.config.ts). That means the first scan would
// otherwise need network — so after the shell loads we fetch it once in the
// background (while online, respecting data-saver) to fill that cache.
import { registerSW } from 'virtual:pwa-register';
import { toast } from './ui/dom.js';

const ENGINE_URL = new URL(`${import.meta.env.BASE_URL}vendor/opencv.js`, location.href).href;

export function initPwa(): void {
  if (!('serviceWorker' in navigator)) return;

  const updateSW = registerSW({
    onNeedRefresh() {
      // Don't reload out from under an in-progress scan — let the teacher choose.
      showUpdateBanner(() => updateSW(true));
    },
    onOfflineReady() {
      toast('Installed — works offline');
    },
    onRegisteredSW() {
      void warmEngine();
    },
  });
}

let warmed = false;
async function warmEngine(): Promise<void> {
  if (warmed) return;
  try {
    if (!navigator.onLine) return;
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    if (conn?.saveData) return; // respect Data Saver — don't pull 10 MB silently
    if (await engineCached()) {
      warmed = true;
      return;
    }
    await whenIdle();
    if (!navigator.onLine) return;
    const res = await fetch(ENGINE_URL); // CacheFirst SW route caches it on the way through
    if (res.ok) {
      warmed = true;
      toast('Scanner ready — you can now scan offline');
    }
  } catch {
    /* best effort — the engine still loads on first scan when online */
  }
}

async function engineCached(): Promise<boolean> {
  if (!('caches' in window)) return false;
  for (const name of await caches.keys()) {
    if (!/opencv/i.test(name)) continue;
    const c = await caches.open(name);
    if (await c.match(ENGINE_URL)) return true;
  }
  return false;
}

function whenIdle(): Promise<void> {
  return new Promise((resolve) => {
    const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
    if (ric) ric(() => resolve(), { timeout: 4000 });
    else setTimeout(resolve, 1200);
  });
}

function showUpdateBanner(onReload: () => void): void {
  document.querySelector('.update-banner')?.remove();
  const el = document.createElement('div');
  el.className = 'update-banner';
  el.setAttribute('role', 'status');
  el.innerHTML = `<span>A new version is available.</span>
    <button type="button" class="btn btn-sm" data-reload>Reload</button>
    <button type="button" class="update-x" data-dismiss aria-label="Dismiss">&times;</button>`;
  el.querySelector('[data-reload]')!.addEventListener('click', onReload);
  el.querySelector('[data-dismiss]')!.addEventListener('click', () => el.remove());
  document.body.appendChild(el);
}
